import React, { useState, useCallback, useRef } from 'react';
import { OpenAIService } from '../../shared/services/openai.service';
import { useIngresosOptimistic } from '../../modules/ingresos/hooks/useIngresosOptimistic';
import { useEgresosOptimistic } from '../../modules/egresos/hooks/useEgresosOptimistic';
import { useAuth } from '../../modules/auth/hooks/useAuth';
import Card from '../base/Card';

interface TransactionData {
  fecha: string;
  descripcion: string;
  monto: number;
  tipo: 'ingreso' | 'egreso';
  categoria?: string;
}

interface ProcessingResult {
  fileName: string;
  transactions: TransactionData[];
  status: 'success' | 'error';
  error?: string;
}

export const PDFUploader: React.FC = () => {
  const { user } = useAuth();
  const { createIngreso } = useIngresosOptimistic();
  const { createEgreso } = useEgresosOptimistic();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [processingStep, setProcessingStep] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openAIService = React.useMemo(() => new OpenAIService(), []);

  const processFiles = useCallback(async (files: File[]) => {
    if (!user) {
      alert('Debes estar autenticado para procesar PDFs');
      return;
    }

    setIsProcessing(true);
    setResults([]);
    setProcessingStep('Verificando configuración...');

    try {
      // Verificar configuración de OpenAI
      if (!openAIService.isConfigured()) {
        throw new Error('OpenAI no está configurado. Verifica tu API key en las variables de entorno.');
      }

      setProcessingStep('OpenAI configurado correctamente');
      const processedResults: ProcessingResult[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProcessingStep(`Procesando ${file.name}... (${i + 1}/${files.length})`);

        try {
          setProcessingStep(`Extrayendo texto de ${file.name}...`);
          // Convertir archivo a texto (simulado - en producción usarías una librería de PDF)
          const fileText = await readPDFAsText(file);
          
          setProcessingStep(`Enviando a OpenAI para análisis...`);
          // Procesar con OpenAI
          const response = await openAIService.processBankDocument(fileText);
          
          // Extraer transacciones de la respuesta
          const transactions: TransactionData[] = [
            ...response.ingresos.map(ingreso => ({
              fecha: ingreso.fecha,
              descripcion: ingreso.concepto,
              monto: ingreso.monto,
              tipo: 'ingreso' as const,
              categoria: ingreso.categoria
            })),
            ...response.egresos.map(egreso => ({
              fecha: egreso.fecha,
              descripcion: egreso.concepto,
              monto: egreso.monto,
              tipo: 'egreso' as const,
              categoria: egreso.categoria
            }))
          ];
          
          processedResults.push({
            fileName: file.name,
            transactions,
            status: 'success'
          });

        } catch (error) {
          console.error(`Error processing ${file.name}:`, error);
          processedResults.push({
            fileName: file.name,
            transactions: [],
            status: 'error',
            error: error instanceof Error ? error.message : 'Error desconocido'
          });
        }
      }

      setResults(processedResults);
      setProcessingStep('Procesamiento completado');

    } catch (error) {
      console.error('Error in batch processing:', error);
      
      let errorMessage = 'Error durante el procesamiento';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      // Crear un resultado de error para mostrar en la UI
      setResults([{
        fileName: 'Error de procesamiento',
        transactions: [],
        status: 'error',
        error: errorMessage
      }]);
      
      setProcessingStep(`Error: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  }, [user, openAIService]);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const pdfFiles = Array.from(files).filter(file => 
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );
    
    if (pdfFiles.length === 0) {
      alert('Por favor selecciona solo archivos PDF');
      return;
    }

    processFiles(pdfFiles);
  }, [processFiles]);

  // Función simulada para leer PDF - para implementación real usar un servicio backend
  const readPDFAsText = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          // Simulación de contenido de PDF bancario para demo
          const simulatedContent = `
RESUMEN BANCARIO - ${file.name}
Fecha: ${new Date().toLocaleDateString()}

MOVIMIENTOS:
01/10/2024 - Sueldo - Ingreso: $450,000.00 - Categoria: Trabajo
03/10/2024 - Supermercado - Egreso: $85,000.00 - Categoria: Alimentacion  
05/10/2024 - Freelance - Ingreso: $120,000.00 - Categoria: Trabajo Extra
07/10/2024 - Gasolina - Egreso: $35,000.00 - Categoria: Transporte
10/10/2024 - Dividendos - Ingreso: $25,000.00 - Categoria: Inversiones
12/10/2024 - Restaurant - Egreso: $45,000.00 - Categoria: Alimentacion
15/10/2024 - Alquiler - Egreso: $180,000.00 - Categoria: Vivienda
18/10/2024 - Servicios - Egreso: $35,000.00 - Categoria: Servicios
20/10/2024 - Bonus - Ingreso: $75,000.00 - Categoria: Trabajo

RESUMEN:
Total Ingresos: $670,000.00
Total Egresos: $380,000.00
Balance: $290,000.00
          `;
          resolve(simulatedContent);
        } catch {
          reject(new Error('Error al procesar el archivo PDF'));
        }
      };
      reader.onerror = () => {
        reject(new Error('Error al leer el archivo'));
      };
      reader.readAsText(file);
    });
  };

  const saveTransactions = useCallback(async (transactions: TransactionData[]) => {
    if (!user) return;

    setProcessingStep('Guardando transacciones...');
    
    try {
      for (const transaction of transactions) {
        if (transaction.tipo === 'ingreso') {
          await createIngreso({
            concepto: transaction.descripcion,
            monto: transaction.monto,
            fecha: new Date(transaction.fecha),
            categoria: transaction.categoria || 'PDF Import'
          });
        } else {
          await createEgreso({
            concepto: transaction.descripcion,
            monto: transaction.monto,
            fecha: new Date(transaction.fecha),
            categoria: transaction.categoria || 'PDF Import'
          });
        }
      }
      
      alert(`Se guardaron ${transactions.length} transacciones exitosamente`);
      setResults([]);
      
    } catch (error) {
      console.error('Error saving transactions:', error);
      alert('Error al guardar transacciones: ' + (error instanceof Error ? error.message : 'Error desconocido'));
    }
  }, [user, createIngreso, createEgreso]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  }, [handleFileSelect]);

  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Solo mostrar en modo debug
  const isDebugMode = import.meta.env.VITE_DEBUG_MODE === 'true';
  
  if (!isDebugMode) {
    return null;
  }

  return (
    <Card className="p-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Procesar Resumen Bancario
        </h3>
        <p className="text-gray-600">
          Sube tu PDF de resumen bancario y automáticamente extraemos tus transacciones
        </p>
      </div>

      {/* Diagnóstico de OpenAI */}
      <div className={`mb-4 p-3 rounded-lg border ${
        openAIService.isConfigured() 
          ? 'bg-green-50 border-green-200' 
          : 'bg-red-50 border-red-200'
      }`}>
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-3 h-3 rounded-full ${
            openAIService.isConfigured() ? 'bg-green-500' : 'bg-red-500'
          }`}></div>
          <span className="font-medium">
            Estado de OpenAI: {openAIService.isConfigured() ? 'Configurado ✅' : 'No configurado ❌'}
          </span>
        </div>
        {!openAIService.isConfigured() && (
          <p className="text-red-600 text-xs mt-1">
            API Key de OpenAI no encontrada. Verifica la variable VITE_OPENAI_API_KEY en tu archivo .env
          </p>
        )}
      </div>

      {/* Área de carga */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
          isDragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${isProcessing ? 'pointer-events-none opacity-50' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
          disabled={isProcessing}
        />

        <div className="text-6xl mb-4">📄</div>
        
        {isProcessing ? (
          <div>
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-lg font-medium text-blue-600 mb-2">
              {processingStep}
            </p>
            <p className="text-sm text-gray-500">
              Por favor espera mientras procesamos tu archivo...
            </p>
          </div>
        ) : (
          <div>
            <p className="text-lg font-medium text-gray-700 mb-2">
              Arrastra tu PDF aquí o haz clic para seleccionar
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Soportamos archivos PDF de cualquier banco
            </p>
            <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Seleccionar Archivo
            </button>
          </div>
        )}
      </div>

      {/* Resultados */}
      {results.length > 0 && (
        <div className="mt-8">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Resultados del Procesamiento
          </h4>
          
          {results.map((result, index) => (
            <div key={index} className="mb-6 p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-gray-800">{result.fileName}</h5>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  result.status === 'success' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {result.status === 'success' ? 'Éxito' : 'Error'}
                </span>
              </div>

              {result.status === 'error' ? (
                <p className="text-red-600 text-sm">{result.error}</p>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    Se encontraron {result.transactions.length} transacciones
                  </p>
                  
                  {/* Preview de transacciones */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 max-h-40 overflow-y-auto">
                    {result.transactions.slice(0, 5).map((transaction, txIndex) => (
                      <div key={txIndex} className="flex justify-between items-center py-1 text-sm">
                        <span className="text-gray-700">{transaction.descripcion}</span>
                        <span className={`font-medium ${
                          transaction.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {transaction.tipo === 'ingreso' ? '+' : '-'}${transaction.monto.toLocaleString()}
                        </span>
                      </div>
                    ))}
                    {result.transactions.length > 5 && (
                      <p className="text-xs text-gray-500 text-center pt-2">
                        y {result.transactions.length - 5} transacciones más...
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button 
                      onClick={() => saveTransactions(result.transactions)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      Guardar Todas
                    </button>
                    <button className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm">
                      Revisar Detalle
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Información adicional */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="text-blue-600 text-xl">💡</div>
          <div>
            <h5 className="font-medium text-blue-900 mb-1">Consejos para mejores resultados:</h5>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Asegúrate de que el PDF sea legible y no esté escaneado en baja calidad</li>
              <li>• Los resúmenes más detallados funcionan mejor</li>
              <li>• Revisa siempre los resultados antes de guardar</li>
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
};